import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn';
import Button from '../ui/Button';
import { useBookingModal } from '../booking/BookingModalContext';
import { fetchRooms } from '../../lib/roomsApi';

type Section = 'home' | 'stay' | 'experience' | 'about' | 'contact' | '';

const TRIP_PLANNER_PATHS = [
  '/tourist-attractions',
  '/things-to-do-in-gujarat',
  '/ahmedabad-tourist-place-planner-day-wise',
  '/near-by-shop-and-restaurant',
];

function sectionFor(pathname: string): Section {
  if (pathname === '/') return 'home';
  if (pathname.startsWith('/rooms') || pathname.startsWith('/spaces')) return 'stay';
  if (
    pathname.startsWith('/reviews') ||
    pathname.startsWith('/location') ||
    pathname.startsWith('/blog') ||
    TRIP_PLANNER_PATHS.some((p) => pathname.startsWith(p))
  )
    return 'experience';
  if (pathname.startsWith('/about') || pathname.startsWith('/sustainability') || pathname.startsWith('/faq')) return 'about';
  if (pathname.startsWith('/contact')) return 'contact';
  return '';
}

const navLinkClasses = (active: boolean) =>
  cn(
    'px-3.5 py-2.5 text-[15px] font-semibold rounded-md no-underline transition-colors duration-150',
    'hover:bg-sand hover:text-olive',
    'active:bg-hairline',
    'focus-visible:outline-none focus-visible:shadow-[0_0_0_3px_rgba(75,94,40,0.3)]',
    active ? 'text-ink' : 'text-muted',
  );

const dropdownLinkClasses =
  'block px-3 py-2.5 text-[14.5px] text-ink-soft-2 no-underline rounded-md whitespace-nowrap transition-colors duration-100 hover:bg-sand hover:text-olive hover:font-semibold active:bg-hairline focus-visible:outline-none focus-visible:shadow-[inset_0_0_0_2px_rgba(75,94,40,0.3)]';

interface Leaf {
  to: string;
  label: string;
}

interface DropdownItem extends Leaf {
  /** Nested links (e.g. individual rooms under "Luxury Rooms & Suites") — hover/focus on this row reveals a second flyout panel. */
  submenu?: Leaf[];
}

interface DropdownProps {
  label: string;
  active: boolean;
  items: DropdownItem[];
}

/** Closes an open dropdown/flyout when the user taps/clicks anywhere outside
 * it — the hover-only version had no such need (moving the mouse away did
 * it), but a tap has no equivalent "leave" event. */
function useCloseOnOutsideClick<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const ref = useRef<T>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, onClose]);
  return ref;
}

/** A row with a nested flyout (e.g. individual rooms under "Luxury Rooms &
 * Suites") — the row itself stays a normal link (tap navigates, matching
 * user expectation for real link text), while the chevron is a separate tap
 * target that opens the flyout without navigating. Desktop mouse users keep
 * the original hover-to-preview behavior on top of that. */
function NavSubmenuItem({ item, onNavigate }: { item: DropdownItem; onNavigate: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useCloseOnOutsideClick<HTMLDivElement>(open, () => setOpen(false));

  return (
    <div
      ref={ref}
      className="relative"
      onPointerEnter={(e) => e.pointerType === 'mouse' && setOpen(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setOpen(false)}
    >
      <div className={cn('flex items-center rounded-md', open && 'bg-sand')}>
        <Link
          to={item.to}
          onClick={onNavigate}
          className={cn(dropdownLinkClasses, 'flex-1', open && 'bg-transparent text-olive font-semibold')}
        >
          {item.label}
        </Link>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${item.label} submenu`}
          className="pr-3 pl-1.5 py-2.5 shrink-0 bg-transparent border-none cursor-pointer text-faint hover:text-olive"
        >
          <ChevronRight size={13} className={cn('transition-transform', open && 'rotate-90 text-olive')} />
        </button>
      </div>
      {/* Zero-gap hoverable bridge (padding, not margin) so the cursor never leaves
          this wrapper while crossing from the row into the flyout — a margin gap here
          would flicker the flyout closed on the way over. */}
      <div
        className={cn(
          'absolute top-0 left-full pl-1.5 transition-all duration-150',
          open ? 'opacity-100 visible translate-x-0' : 'opacity-0 invisible translate-x-1',
        )}
      >
        <div className="bg-white border border-hairline shadow-[0_12px_28px_rgba(33,32,28,0.08)] min-w-[210px] p-2 rounded-lg">
          {item.submenu!.map((leaf) => (
            <Link key={leaf.to} to={leaf.to} onClick={onNavigate} className={dropdownLinkClasses}>
              {leaf.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Opens on hover for mouse users (unchanged from before) but also on tap —
 * pure CSS group-hover never fires on a touchscreen, which is exactly the
 * device class this nav's own "lg" breakpoint pushes onto this desktop-style
 * dropdown instead of the touch-friendly hamburger menu (e.g. Android
 * tablets, ≥1024px viewport but no real hover state). */
function NavDropdown({ label, active, items }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const ref = useCloseOnOutsideClick<HTMLDivElement>(open, () => setOpen(false));
  const close = () => setOpen(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <div
      ref={ref}
      className="relative"
      onPointerEnter={(e) => e.pointerType === 'mouse' && setOpen(true)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(navLinkClasses(active), 'flex items-center gap-1 cursor-pointer select-none bg-transparent border-none')}
      >
        {label} <span className="text-[11px] text-faint">▾</span>
      </button>
      <div
        className={cn(
          'absolute top-full left-0 bg-white border border-hairline shadow-[0_12px_28px_rgba(33,32,28,0.08)] min-w-[230px] p-2 rounded-lg',
          'transition-all duration-150',
          open ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-1',
        )}
      >
        {items.map((item) =>
          item.submenu && item.submenu.length > 0 ? (
            <NavSubmenuItem key={item.to} item={item} onNavigate={close} />
          ) : (
            <Link key={item.to} to={item.to} onClick={close} className={dropdownLinkClasses}>
              {item.label}
            </Link>
          ),
        )}
      </div>
    </div>
  );
}

function buildStayItems(roomItems: Leaf[]): DropdownItem[] {
  return [
    { to: '/rooms', label: 'Luxury Rooms & Suites', submenu: roomItems },
    { to: '/spaces', label: 'Spaces & Amenities' },
  ];
}

const TRIP_PLANNER_ITEMS: Leaf[] = [
  { to: '/tourist-attractions', label: 'Tourist Attractions' },
  { to: '/things-to-do-in-gujarat', label: 'Things to Do in Gujarat' },
  { to: '/ahmedabad-tourist-place-planner-day-wise', label: 'Ahmedabad Day-Wise Planner' },
  { to: '/near-by-shop-and-restaurant', label: 'Near-by Shops & Restaurants' },
];

function buildExperienceItems(): DropdownItem[] {
  return [
    { to: '/reviews', label: 'Guest Reviews' },
    { to: '/location', label: 'Location & Map' },
    { to: '/tourist-attractions', label: 'Trip Planners', submenu: TRIP_PLANNER_ITEMS },
    { to: '/blog', label: 'Blog & Travel Guides' },
  ];
}

function buildMobileGroups(roomItems: Leaf[]): { heading: string; items: DropdownItem[] }[] {
  return [
    { heading: '', items: [{ to: '/', label: 'Home' }] },
    {
      heading: 'The Stay',
      items: buildStayItems(roomItems),
    },
    {
      heading: 'Experience',
      items: buildExperienceItems(),
    },
    {
      heading: 'About Us',
      items: [
        { to: '/about', label: 'Our Story & Host' },
        { to: '/sustainability', label: 'Sustainability' },
        { to: '/faq', label: 'FAQ' },
      ],
    },
    { heading: '', items: [{ to: '/contact', label: 'Contact' }] },
  ];
}

function MobileNavGroup({ item, active, onNavigate }: { item: DropdownItem; active: boolean; onNavigate: () => void }) {
  const [open, setOpen] = useState(false);

  if (!item.submenu || item.submenu.length === 0) {
    return (
      <Link to={item.to} onClick={onNavigate} className={navLinkClasses(active) + ' block py-3'}>
        {item.label}
      </Link>
    );
  }

  return (
    <div>
      <div className="flex items-center">
        <Link to={item.to} onClick={onNavigate} className={navLinkClasses(active) + ' flex-1 py-3'}>
          {item.label}
        </Link>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? `Collapse ${item.label}` : `Expand ${item.label}`}
          aria-expanded={open}
          className="w-10 h-10 flex items-center justify-center bg-transparent border-none text-muted cursor-pointer shrink-0"
        >
          <ChevronDown size={16} className={cn('transition-transform', open && 'rotate-180')} />
        </button>
      </div>
      {open && (
        <div className="pl-4 flex flex-col gap-0.5 mb-1">
          {item.submenu.map((leaf) => (
            <Link key={leaf.to} to={leaf.to} onClick={onNavigate} className={navLinkClasses(false) + ' block py-2 text-[14px]'}>
              {leaf.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NavHeader() {
  const { pathname } = useLocation();
  const current = sectionFor(pathname);
  const { openBooking } = useBookingModal();
  const [roomItems, setRoomItems] = useState<Leaf[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    fetchRooms()
      .then((rooms) =>
        setRoomItems(
          rooms
            .filter((r) => r.is_visible)
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((r) => ({ to: `/rooms/${r.slug}`, label: r.short_name || r.name })),
        ),
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <header className="bg-paper border-b border-hairline font-sans">
      <div className="max-w-page mx-auto px-5 md:px-8 flex items-center justify-between h-18 md:h-21 gap-3 md:gap-6">
        <Link to="/" className="flex items-center no-underline shrink-0 min-w-0">
          <img src="/logo.webp" alt="Homeland Stay" width={330} height={112} className="h-12 md:h-14 w-auto shrink-0" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          <Link to="/" className={navLinkClasses(current === 'home')}>
            Home
          </Link>
          <NavDropdown label="The Stay" active={current === 'stay'} items={buildStayItems(roomItems)} />
          <NavDropdown label="Experience" active={current === 'experience'} items={buildExperienceItems()} />
          <NavDropdown
            label="About Us"
            active={current === 'about'}
            items={[
              { to: '/about', label: 'Our Story & Host' },
              { to: '/sustainability', label: 'Sustainability' },
              { to: '/faq', label: 'FAQ' },
            ]}
          />
          <Link to="/contact" className={navLinkClasses(current === 'contact')}>
            Contact
          </Link>
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={() => openBooking()} size="md" className="!px-4 md:!px-6 !py-2.5 md:!py-3 !text-[13px] md:!text-sm">
            Book Now
          </Button>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-hairline bg-transparent text-ink cursor-pointer"
          >
            {mobileOpen ? <X size={19} /> : <Menu size={19} />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-hairline bg-paper max-h-[calc(100dvh-4.5rem)] overflow-y-auto">
          {/* dvh, not vh: mobile browsers size vh off the largest possible
              viewport (as if the address/toolbar were hidden), so a vh-based
              max-height overflows the real visible area and hides content
              behind the browser's bottom bar. dvh tracks the actual visible
              viewport as that chrome shows/hides. */}
          <nav className="px-5 py-4 flex flex-col gap-1">
            {buildMobileGroups(roomItems).map((group, i) => (
              <div key={i} className={i > 0 ? 'mt-2 pt-2 border-t border-hairline' : ''}>
                {group.heading && (
                  <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-faint px-3.5 pt-2 pb-1">
                    {group.heading}
                  </div>
                )}
                {group.items.map((item) => (
                  <MobileNavGroup key={item.to} item={item} active={pathname === item.to} onNavigate={() => setMobileOpen(false)} />
                ))}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
